import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";

const LOGO_URL = "/dispatch-pay-logo.png";
const LAST_UPDATED = "October 7, 2026";
const CONTACT_EMAIL = "cs@dispatchpaycalc.com";
const COMPANY_LEGAL_NAME = "Ruben Leal (sole proprietor, dba Dispatch Pay Calculator)";
const APP_NAME = "Dispatch Pay Calculator";

export default function Privacy() {
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-[#0B1A3A] border-b border-slate-800">
        <div className="max-w-4xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/home" className="flex items-center gap-2.5">
            <img src={LOGO_URL} alt={APP_NAME} className="h-10 w-auto" />
          </Link>
          <Link to="/signup">
            <Button className="bg-amber-500 hover:bg-amber-600 text-slate-900" data-testid="privacy-signup-btn">Start free trial</Button>
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-16" data-testid="privacy-page">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Legal</div>
        <h1 className="mt-2 text-4xl font-semibold text-slate-900">Privacy Policy</h1>
        <p className="mt-2 text-sm text-slate-500">Last updated: {LAST_UPDATED}</p>

        <div className="mt-10 text-sm leading-relaxed text-slate-700 space-y-5">
          <p>
            This Privacy Policy explains how {COMPANY_LEGAL_NAME} ("we", "us") collects, uses, and shares information when you use {APP_NAME} ("the Service").
          </p>

          <h2 className="text-lg font-semibold text-slate-900 pt-4">1. Information we collect</h2>
          <p>We collect the following information:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Account information:</strong> company name, administrator username, hashed password, email address.</li>
            <li><strong>Workspace content:</strong> drivers, trips, pay statements, deductions, custom fields, and audit logs that you create.</li>
            <li><strong>Billing information:</strong> processed by Stripe. We store a Stripe customer ID and subscription status; we do NOT store your card number, CVC, or billing address.</li>
            <li><strong>Technical data:</strong> IP address, browser user-agent, and timestamps for authentication and security auditing.</li>
          </ul>

          <h2 className="text-lg font-semibold text-slate-900 pt-4">2. How we use information</h2>
          <ul className="list-disc pl-6 space-y-1">
            <li>Operate, maintain, and improve the Service.</li>
            <li>Process subscription payments through Stripe.</li>
            <li>Send transactional emails (signup notifications, approval alerts, billing receipts).</li>
            <li>Detect and prevent abuse, fraud, and unauthorized access.</li>
            <li>Comply with legal obligations.</li>
          </ul>
          <p>
            We do NOT sell your personal information. We do NOT use your Customer Data to train generative AI models. We do NOT share Customer Data with advertisers.
          </p>

          <h2 className="text-lg font-semibold text-slate-900 pt-4">3. Multi-tenant isolation</h2>
          <p>
            The Service is multi-tenant. Every record you create is scoped to your workspace by a unique company identifier. Other workspaces cannot read, write, or enumerate your data through the Service.
          </p>

          <h2 className="text-lg font-semibold text-slate-900 pt-4">4. Third-party processors</h2>
          <p>We rely on the following service providers to operate the Service. Each processes data on our behalf under their own privacy terms:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Stripe, Inc.</strong> — payment processing and subscription management.</li>
            <li><strong>Emergent</strong> — application hosting and transactional email delivery.</li>
            <li><strong>MongoDB, Inc.</strong> — database.</li>
          </ul>

          <h2 className="text-lg font-semibold text-slate-900 pt-4">5. Cookies and local storage</h2>
          <p>
            We use a single first-party JSON Web Token stored in your browser's local storage to keep you signed in. We do not use third-party advertising cookies or analytics trackers.
          </p>

          <h2 className="text-lg font-semibold text-slate-900 pt-4">6. Data retention</h2>
          <p>
            Account and workspace data are retained for as long as your workspace is active. If your subscription is canceled or your workspace is deleted, Customer Data may be retained for up to 30 days before being deleted from live systems. Backup copies may persist for up to 90 days.
          </p>

          <h2 className="text-lg font-semibold text-slate-900 pt-4">7. Your rights</h2>
          <p>Depending on your jurisdiction (e.g., California CCPA, EU/UK GDPR), you may have the right to:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Access the personal data we hold about you.</li>
            <li>Request correction or deletion.</li>
            <li>Request a portable copy of your data.</li>
            <li>Object to or restrict processing.</li>
          </ul>
          <p>
            To exercise any of these rights, email <a href={`mailto:${CONTACT_EMAIL}`} className="text-blue-700 font-semibold">{CONTACT_EMAIL}</a> from the email associated with your workspace. We will respond within 30 days.
          </p>

          <h2 className="text-lg font-semibold text-slate-900 pt-4">8. Security</h2>
          <p>
            We hash passwords with bcrypt, transmit data over HTTPS, and scope every database query by workspace. Despite these measures, no online service can guarantee absolute security. If a breach affects your workspace we will notify you by email without undue delay.
          </p>

          <h2 className="text-lg font-semibold text-slate-900 pt-4">9. Children</h2>
          <p>
            The Service is not directed at children under 18 and we do not knowingly collect data from them. If you believe a child has provided personal data to us, contact us and we will delete it.
          </p>

          <h2 className="text-lg font-semibold text-slate-900 pt-4">10. Changes</h2>
          <p>
            We may update this Privacy Policy. Material changes will be posted here with an updated "Last updated" date. Continued use of the Service after changes become effective constitutes acceptance.
          </p>

          <h2 className="text-lg font-semibold text-slate-900 pt-4">11. Contact</h2>
          <p>
            Privacy questions? Email <a href={`mailto:${CONTACT_EMAIL}`} className="text-blue-700 font-semibold">{CONTACT_EMAIL}</a>.
          </p>
        </div>

        <div className="mt-16 border-t border-slate-200 pt-6 flex items-center justify-between text-xs text-slate-500">
          <Link to="/terms" className="hover:text-slate-900">Terms of Service</Link>
          <Link to="/home" className="hover:text-slate-900">← Back to home</Link>
        </div>
      </main>
    </div>
  );
}
